import { createActionGroup, emptyProps, props } from '@ngrx/store';
import { SubWebsocketMessageRequest } from '@qtek/core/websockets-core';
import {
  CopiedWorkflowInfo,
  EmailDynamicContent,
  EmailTemplate,
  TriggerPayload,
  Workflow,
  WorkflowDetails,
  WorkflowHistoryItem,
  WorkflowPayload,
  WorkflowRun,
} from '@qtek/shared/models';

export const WorkflowActions = createActionGroup({
  source: 'Workflow',
  events: {
    'Subscribe Workflows': props<{
      payload: Omit<SubWebsocketMessageRequest, 'ent' | 'op'>;
      refId: string;
    }>(),
    'Unsubscribe Workflows': props<{
      refId: string;
    }>(),
    'Load Workflows Success': props<{
      workflows: Workflow[];
      refId: string;
    }>(),
    'Load Workflows Failure': props<{ error: Error; refId: string }>(),
    'Create Workflow': props<{
      payload: WorkflowPayload;
      refId: string;
    }>(),
    'Create Workflow Success': props<{
      workflow: WorkflowDetails;
      refId: string;
    }>(),
    'Create Workflow Failure': props<{ error: Error; refId: string }>(),
    'Create Sub Workflow': props<{
      payload: WorkflowPayload & { parentWorkflowId: string };
      refId: string;
      mysid: string;
    }>(),
    'Create Sub Workflow Success': props<{
      subWorkflow: WorkflowDetails;
      refId: string;
      mysid: string;
    }>(),
    'Create Sub Workflow Failure': props<{
      error: Error;
      refId: string;
      mysid: string;
    }>(),
    'Update Workflow': props<{
      payload: WorkflowPayload;
      refId: string;
      id: string;
    }>(),
    'Update Workflow Success': props<{
      workflow: WorkflowDetails;
      refId: string;
    }>(),
    'Update Workflow Failure': props<{ error: Error }>(),
    'Delete Workflow': props<{
      id: string;
      refId: string;
    }>(),
    'Delete Workflow Success': props<{
      id: string;
      refId: string;
    }>(),
    'Delete Workflow Failure': props<{ error: Error }>(),
    'Get Workflow': props<{
      id: string;
      refId: string;
    }>(),
    'Get Workflow Success': props<{
      workflow: WorkflowDetails;
      refId: string;
    }>(),
    'Get Workflow Failure': props<{ error: Error; refId: string }>(),
    'Subscribe Workflow Detail': props<{
      workflowId: string;
      refId: string;
    }>(),
    'Unsubscribe Workflow Detail': props<{
      workflowId: string;
      refId: string;
    }>(),
    'Subscribe Triggers': props<{
      refId: string;
    }>(),
    'Unsubscribe Triggers': props<{
      refId: string;
    }>(),
    'Load Triggers Success': props<{
      triggers: any[];
      refId: string;
    }>(),
    'Load Triggers Failure': props<{ error: Error; refId: string }>(),
    'Create Trigger': props<{
      payload: TriggerPayload;
      refId: string;
    }>(),
    'Create Trigger Success': props<{
      trigger: any;
      refId: string;
    }>(),
    'Create Trigger Failure': props<{ error: Error; refId: string }>(),
    'Update Trigger': props<{
      payload: Partial<TriggerPayload>;
      refId: string;
      id: string;
    }>(),
    'Update Trigger Success': props<{
      trigger: any;
      refId: string;
    }>(),
    'Update Trigger Failure': props<{ error: Error; refId: string }>(),
    'Delete Trigger': props<{
      id: string;
      refId: string;
    }>(),
    'Delete Trigger Success': props<{
      trigger: any;
      refId: string;
    }>(),
    'Delete Trigger Failure': props<{ error: Error; refId: string }>(),
    'Create Workflow Action': props<{
      payload: any;
      refId: string;
      workflowId: string;
    }>(),
    'Create Workflow Action Success': props<{
      task: any;
      workflowId: string;
      refId: string;
    }>(),
    'Remove Workflow Action': props<{
      taskId: string;
      mysid: string;
      workflowId: string;
    }>(),
    'Remove Workflow Action Success': props<{
      taskId: string;
      workflowId: string;
      refId: string;
    }>(),
    'Update Workflow Action': props<{
      payload: any;
      refId: string;
      workflowId: string;
      taskId: string;
    }>(),
    'Update Workflow Action Success': props<{
      task: any;
      workflowId: string;
      refId: string;
    }>(),
    'Update Workflow Action Failure': props<{
      error: Error;
      refId: string;
    }>(),
    'Trigger Workflow': props<{
      triggerId: string;
      refId: string;
    }>(),
    'Trigger Workflow Success': props<{
      workflow: Workflow;
      refId: string;
    }>(),
    'Trigger Workflow Failure': props<{ error: Error; refId: string }>(),
    'Swap Actions': props<{
      draggedStepId: string;
      targetStepId: string;
      refId: string;
      workflowId: string;
    }>(),
    'Swap Actions Success': props<{
      refId: string;
    }>(),
    'Swap Actions Failure': props<{
      error: Error;
      refId: string;
    }>(),
    'Subscribe Workflow Tasks': props<{
      payload: Omit<SubWebsocketMessageRequest, 'ent' | 'op'>;
      mysid: string;
      refId: string;
    }>(),
    'Unsubscribe Workflow Tasks': props<{
      mysid: string;
      refId: string;
    }>(),
    'Load Workflow Task Configurations Failure': props<{
      error: Error;
      mysid: string;
      refId: string;
    }>(),
    'Create Workflow Task Configuration Failure': props<{
      error: Error;
      mysid: string;
      refId: string;
    }>(),
    'Update Workflow Task Configuration Failure': props<{
      error: Error;
      mysid: string;
      refId: string;
    }>(),
    'Delete Workflow Task Configuration Failure': props<{
      error: Error;
      mysid: string;
      refId: string;
    }>(),
    'Subscribe Workflow History': props<{
      payload: Omit<SubWebsocketMessageRequest, 'ent' | 'op'>;
      workflowId: string;
      mysid: string;
    }>(),
    'Unsubscribe Workflow History': props<{
      workflowId: string;
      mysid: string;
    }>(),
    'Load Workflow Runs Success': props<{
      runs: WorkflowRun[];
      workflowId: string;
    }>(),
    'Load Workflow Runs Failure': props<{
      error: Error;
      workflowId: string;
    }>(),
    'Upsert Workflow Run': props<{
      workflowId: string;
      run: WorkflowRun;
    }>(),
    'Get Workflow History By Run': props<{
      runId: string;
      workflowId: string;
      mysid: string;
    }>(),
    'Clear Workflow History': props<{ runId: string }>(),
    'Load Workflow History Success': props<{
      history: WorkflowHistoryItem | null;
      workflowId: string;
      runId: string;
    }>(),
    'Load Workflow History Failure': props<{
      error: Error;
      workflowId: string;
      runId: string;
    }>(),
    'Load Role Participants': props<{
      refId: string;
      roleIds: string[];
    }>(),
    'Load Role Participants Success': props<{
      refId: string;
      participants: {
        internalParticipants: Record<string, number>;
        externalParticipants: Record<string, number>;
        allParticipants: Record<string, number>;
      };
    }>(),
    'Load Role Participants Failure': props<{
      refId: string;
      error: Error;
    }>(),
    'Clear Role Participants': props<{ refId: string }>(),
    'Get Email Dynamic Content': props<{
      taskId?: string;
    }>(),
    'Get Email Dynamic Content Success': props<{
      dynamicContent: EmailDynamicContent;
    }>(),
    'Get Email Dynamic Content Failure': props<{
      error: Error;
    }>(),
    'Get Email Templates': emptyProps(),
    'Get Email Templates Success': props<{
      templates: EmailTemplate[];
    }>(),
    'Get Email Templates Failure': props<{
      error: Error;
    }>(),
    'Set Active Workflow': props<{
      workflow: WorkflowDetails | null;
    }>(),
    'Subscribe My Deals Workflows': emptyProps(),
    'Unsubscribe My Deals Workflows': emptyProps(),
    'Load My Deals Workflows Success': props<{
      workflows: Workflow[];
    }>(),
    'Load My Deals Workflows Failure': props<{
      error: Error;
    }>(),
    'Copy Workflows': props<{
      sourceWorkflowIds: string[];
      targetDealRefId: string;
    }>(),
    'Copy Workflows Success': props<{
      copiedWorkflows: CopiedWorkflowInfo[];
      targetDealRefId: string;
    }>(),
    'Copy Workflows Failure': props<{
      error: Error;
      targetDealRefId: string;
    }>(),
  },
});
